import { useState, useEffect } from "react";
import { orgApi } from "@/services/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { ORG } from "@/constants/testIds";
import { Building2, Network, MapPin, Plus, X, Loader2 } from "lucide-react";

export default function OrganizationSetup() {
  const [tab, setTab] = useState("legal");
  const [legalEntities, setLegalEntities] = useState([]);
  const [operatingUnits, setOperatingUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);

  useEffect(() => {
    Promise.all([orgApi.listLegalEntities(), orgApi.listOperatingUnits(), orgApi.listLocations()])
      .then(([le, ou, loc]) => { setLegalEntities(le.data); setOperatingUnits(ou.data); setLocations(loc.data); })
      .finally(() => setLoading(false));
  }, []);

  const reload = () => {
    Promise.all([orgApi.listLegalEntities(), orgApi.listOperatingUnits(), orgApi.listLocations()])
      .then(([le, ou, loc]) => { setLegalEntities(le.data); setOperatingUnits(ou.data); setLocations(loc.data); });
  };

  const TABS = [
    { id: "legal", label: "Legal Entities", icon: Building2, count: legalEntities.length },
    { id: "ou", label: "Operating Units", icon: Network, count: operatingUnits.length },
    { id: "locations", label: "Locations", icon: MapPin, count: locations.length },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#181818] font-sans">Organization Structure</h1>
        <p className="text-sm text-[#706E6B] mt-1">Configure legal entities, operating units and locations</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              tab === t.id ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "text-[#706E6B] hover:text-[#181818]"
            }`}>
            <t.icon className="w-4 h-4" />
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${tab === t.id ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#E5E5E5] text-[#706E6B]"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? <Skeleton /> : (
        <>
          {/* Legal Entities */}
          {tab === "legal" && (
            <Section title="Legal Entities" testId={ORG.legalEntityList}
              addBtn={<AddBtn onClick={() => setShowForm("le")} testId={ORG.createLegalEntityBtn} />}>
              {legalEntities.map(le => (
                <DataRow key={le.id} title={le.name} subtitle={le.code} meta={[le.country, le.base_currency, le.registration_number].filter(Boolean).join(" · ")} status={le.status} />
              ))}
              {legalEntities.length === 0 && <Empty msg="No legal entities configured" />}
            </Section>
          )}

          {/* Operating Units */}
          {tab === "ou" && (
            <Section title="Operating Units" testId={ORG.operatingUnitList}
              addBtn={<AddBtn onClick={() => setShowForm("ou")} />}>
              {operatingUnits.map(ou => (
                <DataRow key={ou.id} title={ou.name} subtitle={ou.code}
                  meta={[ou.unit_type, ou.parent_id ? `Parent: ${operatingUnits.find(x => x.id === ou.parent_id)?.name || ou.parent_id}` : "Root"].join(" · ")}
                  status={ou.status} badge={ou.unit_type} />
              ))}
              {operatingUnits.length === 0 && <Empty msg="No operating units configured" />}
            </Section>
          )}

          {/* Locations */}
          {tab === "locations" && (
            <Section title="Locations" testId={ORG.locationList}
              addBtn={<AddBtn onClick={() => setShowForm("loc")} />}>
              {locations.map(loc => (
                <DataRow key={loc.id} title={loc.name} subtitle={loc.code}
                  meta={[loc.location_type, loc.city, loc.country].filter(Boolean).join(" · ")}
                  status={loc.status} badge={loc.location_type} />
              ))}
              {locations.length === 0 && <Empty msg="No locations configured" />}
            </Section>
          )}
        </>
      )}

      {/* Forms */}
      {showForm === "le" && (
        <Modal title="Add Legal Entity" onClose={() => setShowForm(null)}>
          <LegalEntityForm onSuccess={() => { setShowForm(null); reload(); }} />
        </Modal>
      )}
      {showForm === "ou" && (
        <Modal title="Add Operating Unit" onClose={() => setShowForm(null)}>
          <OperatingUnitForm legalEntities={legalEntities} operatingUnits={operatingUnits} onSuccess={() => { setShowForm(null); reload(); }} />
        </Modal>
      )}
      {showForm === "loc" && (
        <Modal title="Add Location" onClose={() => setShowForm(null)}>
          <LocationForm onSuccess={() => { setShowForm(null); reload(); }} />
        </Modal>
      )}
    </div>
  );
}

function LegalEntityForm({ onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", registered_name: "", registration_number: "", tax_identifier: "", country: "IN", base_currency: "INR", status: "ACTIVE" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await orgApi.createLegalEntity(form); toast.success("Legal Entity created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {[["code","Code","LE-001"],["name","Name","Prashasta Events"],["registered_name","Registered Name","Prashasta Events Pvt Ltd"],["registration_number","Reg. Number","CIN-U74999..."],["tax_identifier","Tax ID / GSTIN",""],].map(([k,l,p]) => (
        <FormField key={k} label={l} value={form[k]} onChange={v => setForm({...form,[k]:v})} placeholder={p} required={["code","name"].includes(k)} />
      ))}
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Country" value={form.country} onChange={v => setForm({...form,country:v})} options={[["IN","India"],["US","USA"],["GB","UK"],["AE","UAE"]]} />
        <SelectField label="Currency" value={form.base_currency} onChange={v => setForm({...form,base_currency:v})} options={[["INR","INR"],["USD","USD"],["EUR","EUR"],["GBP","GBP"]]} />
      </div>
      <SubmitBtn loading={loading} label="Create Legal Entity" />
    </form>
  );
}

function OperatingUnitForm({ legalEntities, operatingUnits, onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", unit_type: "OPERATING_UNIT", parent_id: "", legal_entity_id: "", status: "ACTIVE" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await orgApi.createOperatingUnit({...form, parent_id: form.parent_id || null, legal_entity_id: form.legal_entity_id || null}); toast.success("Operating Unit created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <FormField label="Code" value={form.code} onChange={v => setForm({...form,code:v})} placeholder="OU-001" required />
      <FormField label="Name" value={form.name} onChange={v => setForm({...form,name:v})} placeholder="Sales Department" required />
      <SelectField label="Unit Type" value={form.unit_type} onChange={v => setForm({...form,unit_type:v})}
        options={[["OPERATING_UNIT","Operating Unit"],["REGION","Region"],["BRANCH","Branch"],["BUSINESS_UNIT","Business Unit"],["DEPARTMENT","Department"],["TEAM","Team"]]} />
      <SelectField label="Legal Entity (optional)" value={form.legal_entity_id} onChange={v => setForm({...form,legal_entity_id:v})}
        options={[["","—None—"],...legalEntities.map(le=>[le.id,le.name])]} />
      <SelectField label="Parent Unit (optional)" value={form.parent_id} onChange={v => setForm({...form,parent_id:v})}
        options={[["","—Root—"],...operatingUnits.map(ou=>[ou.id,ou.name])]} />
      <SubmitBtn loading={loading} label="Create Operating Unit" />
    </form>
  );
}

function LocationForm({ onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", location_type: "CITY", country: "IN", city: "", address: "", status: "ACTIVE" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await orgApi.createLocation(form); toast.success("Location created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <FormField label="Code" value={form.code} onChange={v => setForm({...form,code:v})} placeholder="LOC-HYD-001" required />
      <FormField label="Name" value={form.name} onChange={v => setForm({...form,name:v})} placeholder="Hyderabad" required />
      <SelectField label="Location Type" value={form.location_type} onChange={v => setForm({...form,location_type:v})}
        options={[["CITY","City"],["COUNTRY","Country"],["OFFICE","Office"],["VENUE_BASE","Venue Base"],["OTHER","Other"]]} />
      <FormField label="City" value={form.city} onChange={v => setForm({...form,city:v})} placeholder="Hyderabad" />
      <SubmitBtn loading={loading} label="Create Location" />
    </form>
  );
}

// Shared UI helpers
function Section({ title, children, addBtn, testId }) {
  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#444444] font-sans">{title}</h2>
        {addBtn}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function DataRow({ title, subtitle, meta, status, badge }) {
  return (
    <div className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#181818]">{title}</p>
        <p className="text-xs font-mono text-[#706E6B] mt-0.5">{subtitle}</p>
        {meta && <p className="text-xs text-[#706E6B] mt-0.5">{meta}</p>}
      </div>
      <div className="flex items-center gap-2 ml-4">
        {badge && <span className="text-[10px] font-mono text-[#706E6B] px-1.5 py-0.5 rounded bg-[#F3F3F3] border border-[#E5E5E5]">{badge}</span>}
        <StatusBadge status={status} />
      </div>
    </div>
  );
}
function AddBtn({ onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5] transition-all">
      <Plus className="w-3.5 h-3.5" /> Add
    </button>
  );
}
function Empty({ msg }) {
  return <div className="text-center py-8 text-sm text-[#706E6B] bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">{msg}</div>;
}
function Skeleton() {
  return <div className="space-y-2 animate-pulse">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-[#F3F3F3] rounded-xl" />)}</div>;
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-[#F3F3F3] border border-[#E5E5E5] rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-[#181818] font-sans">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function FormField({ label, value, onChange, placeholder, required, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors" />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm focus:outline-none focus:border-[#0176D3] transition-colors">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function SubmitBtn({ loading, label }) {
  return (
    <button type="submit" disabled={loading}
      className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#0176D3] to-[#014486] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2">
      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : label}
    </button>
  );
}
